import { Alert, CircularProgress, Snackbar, Backdrop } from 'MaterialUIComponents';
import { ConfigService, IConfigServiceInjectedProps } from '$State/ConfigFreezerService';
import { DeviceAssociationService, IDeviceAssociationServiceInjectedProps } from '$State/DeviceAssociationFreezerService';
import { VideoEventWorkflowStatusIdEnum } from '$Generated/api';
import { IIntegrationPartnerDataInjectedProps, IntegrationPartnerDataService } from '$State/IntegrationPartnerDataFreezerService';
import { IVideoEventServiceInjectedProps, VideoEventService } from '$State/VideoEventFreezerService';
import { IVideoRecallFilterServiceInjectedProps, VideoRecallFilterService } from '$State/VideoRecallEventFilterFreezerService';
import { IVideoRecallServiceInjectedProps, VideoRecallService } from '$State/VideoRecallFreezerService';
import { forEach } from 'lodash';
import { PageHeader } from '$Components/Shared/PageHeader';
import { React, _, bind } from 'Imports';
import { RolesEnum, canEditByRole } from '$Externals/VerifyRole';
import { VideoEventCardList } from '$Components/VideoRecall/VideoEventCardList';
import { VideoEventList } from '$Components/VideoRecall/VideoEventList';
import { VideoRecallRequestForm } from '$Components/VideoRecall/VideoRecallRequestForm';
import { TermsOfUse } from '$Components/Shared/TermsOfUse';
import ConfigurableFilterBar, { FilterConfig, FilterType } from '$Components/FilterComponents/ConfigurableFilterBar';
import { toDate } from '$Utilities/dateUtility';
import { getFormattedDeviceResults } from '$Utilities/filterUtilities';
import moment = require('moment');
import { FiltersButton, ViewModeToggleButton } from '$Components/FilterComponents/Buttons';
import { RequestVideoButton } from './RequestVideoButton';
import { RequestVideoDrawer } from '$Components/VideoRecall/RequestVideoDrawer';

interface IFilterOptions {
    eventType?: number;
    driverName?: string[];
    startDate?: Date;
    endDate?: Date;
    vehicleId?: string[];
    status?: VideoEventWorkflowStatusIdEnum[];
}

interface IVideoRecallBaseProps {
    onSelectVideoEvent: (value: string, goBack: boolean) => void;
    groupsFilter?: string;
    isShowInactiveVehiclesSplitOn?: boolean;
    isVideoRecallPreviewOn?: boolean;
    isVideoRecallRedesignV2SplitOn?: boolean;
}

type IVideoRecallProps = IVideoRecallBaseProps &
    IVideoRecallServiceInjectedProps &
    IIntegrationPartnerDataInjectedProps &
    IVideoRecallFilterServiceInjectedProps &
    IVideoEventServiceInjectedProps &
    IDeviceAssociationServiceInjectedProps &
    IConfigServiceInjectedProps;

interface IVideoRecallState {
    showMobileFilterDrawer: boolean;
    requestVideoVisible: boolean;
    isMobileView: boolean;
    snackOpen: boolean;
    snackMessage: string;
    interval: NodeJS.Timeout | undefined;
    groupsFilter: string;
}

const styles = require('./VideoRecall.scss') as {
    main: string;
    contentContainer: string;
    content: string;
    noVideos: string;
    filterContainer: string;
    filterContainerMobile: string;
    filterBar: string;
    filterBackdrop: string;
    searchButton: string;
    buttonContainer: string;
    showRequestButton: string;
    trayTitle: string;
    trayClose: string;
    buttonIcon: string;
    activeButton: string;
    filterButtonGeotab: string;
    filterButtonEncompass: string;
    requestVideoRightSlot: string;
};

class _VideoRecallPage extends React.Component<IVideoRecallProps, IVideoRecallState> {
    filterContainerDiv: any;

    state: IVideoRecallState = {
        showMobileFilterDrawer: false,
        requestVideoVisible: false,
        isMobileView: false,
        snackOpen: false,
        snackMessage: '',
        interval: undefined,
        groupsFilter: this.props.integrationPartnerData.getGroupsFilter(),
    };

    async componentDidMount(): Promise<void> {
        // if split is off, include inactive vehicles by default
        if (!this.props.isShowInactiveVehiclesSplitOn) {
            this.props.videoRecall.setIncludeInactiveVehicles(true);
        }
        window.addEventListener('resize', this.onWindowResize);
        // Subscribe to store updates
        this.props.integrationPartnerData.on('update', this.handleStoreUpdate);
        await this.getVideoRecalls();
        this.props.integrationPartnerData.getDevices();
        this.onWindowResize();

        const { videoRecallResultsFinal } = this.props.videoRecall.getState();
        const data = videoRecallResultsFinal.length > 0 ? videoRecallResultsFinal : [];
        this.checkPendings(data);
    }

    @bind
    async getVideoRecalls(): Promise<void> {
        this.props.videoRecallEventFilter.setPageNumber(1);

        const eventFilter = _.cloneDeep(this.props.videoRecallEventFilter.getVideoRecallEventFilter());

        eventFilter.groupsFilter = this.props.integrationPartnerData.getGroupsFilter();
        eventFilter.includeInactiveVehicles = this.props.videoRecall.getIncludeInactiveVehicles();

        const prevVehicleFilter = eventFilter.vehicleId;

        eventFilter.vehicleId = [];

        const integrationVehicles = this.props.integrationPartnerData.getState().deviceResults;

        forEach(integrationVehicles.data, function (v) {
            if (v.id && prevVehicleFilter && prevVehicleFilter.indexOf(v.id) > -1) {
                eventFilter.vehicleId?.push(v.id);
            }
        });

        this.props.videoRecallEventFilter.updateFilterOptions(
            eventFilter.eventType,
            eventFilter.driverName,
            eventFilter.vehicleId,
            eventFilter.startDate,
            eventFilter.endDate,
            eventFilter.groupsFilter,
            eventFilter.includeInactiveVehicles,
        );

        await this.props.videoRecall.getFilteredVideoRecalls(this.props.videoRecallEventFilter.getVideoRecallEventFilter());
        await this.getFullListOfSucessRecallIds();
    }

    async componentWillUnmount(): Promise<void> {
        window.removeEventListener('resize', this.onWindowResize);
        // Unsubscribe from store updates
        this.props.integrationPartnerData.off('update', this.handleStoreUpdate);
        if (this.state.interval != undefined) {
            clearInterval(this.state.interval);
        }
    }

    @bind
    handleEventLayoutChange(e: any, value: 'tile' | 'list'): void {
        if (value != null) {
            this.props.videoRecallEventFilter.setPageNumber(1);
            this.props.videoRecallEventFilter.setSortFields('date', false);
            const eventFilter = this.props.videoRecallEventFilter.getVideoRecallEventFilter();
            this.props.videoRecall.getFilteredVideoRecalls(eventFilter);
            this.props.videoRecall.setVideoRecallViewType(value);
        }
    }

    @bind
    async handleFilterEvents(filterOptions: IFilterOptions): Promise<void> {
        this.props.videoRecallEventFilter.setPageNumber(1);
        this.props.videoRecallEventFilter.updateFilterOptions(
            filterOptions.eventType,
            filterOptions.driverName,
            filterOptions.vehicleId,
            filterOptions.startDate,
            filterOptions.endDate,
            IntegrationPartnerDataService.getGroupsFilter(),
            this.props.videoRecall.getIncludeInactiveVehicles(),
        );

        const eventFilter = this.props.videoRecallEventFilter.getVideoRecallEventFilter();
        await this.props.videoRecall.getFilteredVideoRecalls(eventFilter);
        await this.getFullListOfSucessRecallIds();
    }

    @bind
    async getFullListOfSucessRecallIds(): Promise<number[]> {
        const recallFilter = {
            ...this.props.videoRecallEventFilter.getVideoRecallEventFilter(),
            eventTypes: [5],
            showVideoPipelineSuccessOnly: true,
        };

        return await this.props.videoEvents.getListOfEventIds(recallFilter);
    }

    @bind
    getVideoRecallFilterConfig(): FilterConfig[] {
        const { deviceResults } = this.props.integrationPartnerData.getState();
        const eventFilter = this.props.videoRecallEventFilter.getVideoRecallEventFilter();
        const includeInactiveVehicles = this.props.isShowInactiveVehiclesSplitOn
            ? this.props.videoRecall.getIncludeInactiveVehicles()
            : false;

        const filterStartDate = toDate(eventFilter.startDate || new Date());
        const filterEndDate = toDate(eventFilter.endDate || new Date());
        const formattedDevices = getFormattedDeviceResults(deviceResults, filterStartDate, filterEndDate, includeInactiveVehicles);

        const filterConfig: FilterConfig[] = [
            {
                type: FilterType.Multiselect,
                key: 'vehicles',
                label: 'Vehicle',
                items: formattedDevices,
                selectedValues: eventFilter.vehicleId || [],
                disabled: formattedDevices.length === 0,
                searchable: true,
                toggleable: true,
                toggleText: 'Include Inactive',
                toggleStatus: includeInactiveVehicles,
                onToggleChange: this.onChangeIncludeInactiveVehicles,
            },
            {
                type: FilterType.DateRange,
                key: 'dateRange',
                startDate: eventFilter.startDate ? moment(eventFilter.startDate) : undefined,
                endDate: eventFilter.endDate ? moment(eventFilter.endDate) : undefined,
                onDateRangeApply: this.handleDateRangeChange,
            },
        ];

        return filterConfig;
    }

    @bind
    handleVideoRecallFilterChange(filterKey: string, value: any): void {
        const eventFilter = this.props.videoRecallEventFilter.getVideoRecallEventFilter();

        if (filterKey === 'vehicles') {
            const updatedFilterItems = _.sortBy(value.map((v: any) => v.toString()));
            this.handleFilterEvents({
                ...eventFilter,
                vehicleId: updatedFilterItems,
            });
        }
    }

    @bind
    handleDateRangeChange(startDate: moment.Moment | Date | undefined, endDate: moment.Moment | Date | undefined): void {
        const eventFilter = this.props.videoRecallEventFilter.getVideoRecallEventFilter();
        const startDateValue = startDate instanceof Date ? startDate : startDate?.toDate();
        const endDateValue = endDate instanceof Date ? endDate : endDate?.toDate();

        this.handleFilterEvents({
            ...eventFilter,
            startDate: startDateValue,
            endDate: endDateValue,
        });
    }

    @bind
    async gridSort(sortBy: string, sortAsc: boolean): Promise<void> {
        this.props.videoRecallEventFilter.setPageNumber(1);
        this.props.videoRecallEventFilter.setSortFields(sortBy, sortAsc);
        const eventFilter = this.props.videoRecallEventFilter.getVideoRecallEventFilter();
        await this.props.videoRecall.getFilteredVideoRecalls(eventFilter);
    }

    @bind
    async _onPageNumberChanged(newPage: number): Promise<void> {
        this.props.videoRecallEventFilter.setPageNumber(newPage);
        const eventFilter = this.props.videoRecallEventFilter.getVideoRecallEventFilter();
        await this.props.videoRecall.getFilteredVideoRecalls(eventFilter, { append: true });
    }

    @bind
    toggleFilters(): void {
        const { showMobileFilterDrawer: filtersVisible } = this.state;
        this.setState({
            showMobileFilterDrawer: !filtersVisible,
            requestVideoVisible: false,
        });
    }

    @bind
    async toggleRequestVideo(): Promise<void> {
        const { requestVideoVisible } = this.state;
        if (!requestVideoVisible && this.props.isVideoRecallPreviewOn) {
            await this.props.deviceAssociation.getDeviceAssociations();
        }
        this.setState({
            requestVideoVisible: !requestVideoVisible,
            showMobileFilterDrawer: false,
        });
    }

    @bind
    async loadVideoEventsOnScroll(): Promise<void> {
        if (!this.props.videoRecall.getState().videoRecallResults.isFetching) {
            const eventFilter = this.props.videoRecallEventFilter.getVideoRecallEventFilter();
            const currentPage = eventFilter.currentPage;
            if (currentPage && currentPage !== undefined) {
                let newPage = currentPage;
                newPage++;
                setTimeout(async (): Promise<void> => {
                    await this._onPageNumberChanged(newPage);
                }, 1500);
            }
        }
    }

    @bind
    onWindowResize(): void {
        if (window.innerWidth < 1150) {
            this.setState({ isMobileView: true, showMobileFilterDrawer: false });
        } else {
            this.setState({ isMobileView: false, showMobileFilterDrawer: false });
        }
    }

    @bind
    async deleteVideoRecall(eventId: number): Promise<void> {
        const filter = this.props.videoRecallEventFilter.getVideoRecallEventFilter();
        await this.props.videoRecall.deleteVideoRecall(eventId);
        await this.props.videoRecall.getFilteredVideoRecalls(filter);
        await this.getFullListOfSucessRecallIds();
    }

    @bind
    async handleOnDeletedVideo(): Promise<void> {
        const filter = this.props.videoRecallEventFilter.getVideoRecallEventFilter();
        await this.props.videoRecall.getFilteredVideoRecalls(filter);
        await this.getFullListOfSucessRecallIds();
    }

    @bind
    async updateVideoEventWorkflowStatus(eventId: number | undefined): Promise<void> {
        if (!canEditByRole([RolesEnum.videoEventEdit, RolesEnum.encompassVideo])) return;
        try {
            // fetchResults rejects on failure, so the card's label is only updated once the request has
            // actually succeeded -- otherwise it would show In_Progress even if the update failed.
            await this.props.videoEvents.updateVideoEventWorkflowStatus(eventId, 'In_Progress');
            // The API call's own local-state update lands on a different freezer's list (videoEvents'
            // videoEventResultsFinal), which isn't what backs these cards, so this is still needed to
            // update the card's label without a full card-list re-query.
            this.props.videoRecall.updateLocalVideoEventStatus('In_Progress', eventId);
        } catch {
            // onError already flags hasError on the videoEvents freezer; nothing further to do here --
            // just leave the card's displayed status as it was.
        }
    }

    @bind
    async promoteVideo(eventId: number): Promise<void> {
        await this.props.videoRecall.PromoteVideoRecall(eventId);
        this.props.videoRecallEventFilter.setPageNumber(1);
        const eventFilter = this.props.videoRecallEventFilter.getVideoRecallEventFilter();
        this.snackOpen();
        this.props.videoRecall.getFilteredVideoRecalls(eventFilter);
        await this.getFullListOfSucessRecallIds();
    }

    // Same as promoteVideo, but without the legacy this.snackOpen() call -- the redesigned list/card
    // views show their own success/failure snackbar per action, so triggering the legacy one here too
    // would show both at once.
    @bind
    async promoteVideoRedesign(eventId: number): Promise<void> {
        await this.props.videoRecall.PromoteVideoRecall(eventId);
        this.props.videoRecallEventFilter.setPageNumber(1);
        const eventFilter = this.props.videoRecallEventFilter.getVideoRecallEventFilter();
        this.props.videoRecall.getFilteredVideoRecalls(eventFilter);
        await this.getFullListOfSucessRecallIds();
    }

    @bind
    async handleOnPromotedVideo(): Promise<void> {
        this.props.videoRecallEventFilter.setPageNumber(1);
        const eventFilter = this.props.videoRecallEventFilter.getVideoRecallEventFilter();
        this.props.videoRecall.getFilteredVideoRecalls(eventFilter);
        await this.getFullListOfSucessRecallIds();
    }

    @bind
    snackOpen(): void {
        this.setState({ snackOpen: true, snackMessage: 'Video Event Promoted' });
    }

    @bind
    handleSnackClose(): void {
        this.setState({ snackOpen: false, snackMessage: '' });
    }

    @bind
    onChangeIncludeInactiveVehicles(includeInactives?: boolean) {
        this.props.videoRecall.setIncludeInactiveVehicles(includeInactives);
    }

    async componentDidUpdate(prevProps: Readonly<IVideoRecallProps>): Promise<void> {
        if (this.props.groupsFilter !== prevProps.groupsFilter) {
            await DeviceAssociationService.getDeviceAssociations();
            await IntegrationPartnerDataService.getDevices();
            await this.getVideoRecalls();
        }

        const { videoRecallResultsFinal } = this.props.videoRecall.getState();
        const data = videoRecallResultsFinal.length > 0 ? videoRecallResultsFinal : [];
        this.checkPendings(data);
    }

    @bind
    handleStoreUpdate(): void {
        const newGroupsFilter = this.props.integrationPartnerData.getGroupsFilter();
        if (!_.isEqual(this.state.groupsFilter, newGroupsFilter)) {
            this.setState({ groupsFilter: newGroupsFilter });
        }
    }

    checkPendings(data: any[]): void {
        const pendings = data.filter((i) => i.workflowStep != 'Success' && i.workflowStep != 'Failed').length;

        if (pendings > 0 && this.state.interval == undefined) {
            const interval = setInterval(async () => {
                if (this.props.videoRecall.getState().videoRecallResults.isFetching) {
                    return;
                }
                const eventFilter = this.props.videoRecallEventFilter.getVideoRecallEventFilter();
                eventFilter.itemsPerPage = (eventFilter.currentPage ?? 1) * (eventFilter.itemsPerPage ?? 25);
                eventFilter.currentPage = 1;
                await this.props.videoRecall.getFilteredVideoRecalls(eventFilter);
            }, 120000);
            this.setState({ interval });
        } else if (pendings == 0 && this.state.interval != undefined) {
            clearInterval(this.state.interval);
            this.setState({ interval: undefined });
        }
    }

    render(): JSX.Element {
        const freezer = this.props.videoRecall.getState();
        const {
            videoRecallResults: videoRecallResults,
            videoRecallResultsFinal: videoRecallResultsFinal,
            videoRecallViewType: videoRecallViewType,
        } = this.props.videoRecall.getState();

        const isTileView = videoRecallViewType === 'tile';
        const data = videoRecallResultsFinal.length > 0 ? videoRecallResultsFinal : [];

        const eventFilter = this.props.videoRecallEventFilter.getVideoRecallEventFilter();
        const hasMorePagesToLoad =
            !freezer.hasInfiniteScrollError && (videoRecallResults.data?.currentPage ?? 0) < (videoRecallResults.data?.totalPages ?? 0)
                ? true
                : false;

        // if shouldLoadNextPage is true, the first page had all cards filtered out for tile view on the first page,
        // but there is more on the next page. We want to auto load the next page in this situation.
        const shouldLoadNextPage = isTileView && data.length === 0 && hasMorePagesToLoad;

        return (
            <div className={styles.main} id="videoRecallPage">
                <Snackbar
                    anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
                    open={this.state.snackOpen}
                    onClose={this.handleSnackClose}
                    autoHideDuration={4000}
                    style={{ position: 'absolute' }}
                >
                    <Alert elevation={24} variant="filled" onClose={this.handleSnackClose} severity="success" color="success">
                        {this.state.snackMessage}
                    </Alert>
                </Snackbar>
                <TermsOfUse />

                <PageHeader pageTitle={'Video Recall'} fixedHeight={false} onSelectVideoEvent={this.props.onSelectVideoEvent} />

                <div className={styles.contentContainer}>
                    <ConfigurableFilterBar
                        filters={this.getVideoRecallFilterConfig()}
                        onFilterChange={this.handleVideoRecallFilterChange}
                        isMobileView={this.state.isMobileView}
                        slotProps={{
                            mobile: {
                                left: <FiltersButton onClick={this.toggleFilters} />,
                                right: (
                                    <>
                                        <RequestVideoButton
                                            onClick={this.toggleRequestVideo}
                                            videoRecallPreviewEnabled={this.props.isVideoRecallPreviewOn}
                                        />
                                        <ViewModeToggleButton
                                            handleEventLayoutChange={this.handleEventLayoutChange}
                                            viewType={videoRecallViewType}
                                        />
                                    </>
                                ),
                            },
                            desktop: {
                                right: (
                                    <div className={styles.requestVideoRightSlot}>
                                        {this.props.isVideoRecallPreviewOn && (
                                            <RequestVideoButton
                                                onClick={this.toggleRequestVideo}
                                                videoRecallPreviewEnabled={this.props.isVideoRecallPreviewOn}
                                            />
                                        )}
                                        <ViewModeToggleButton
                                            handleEventLayoutChange={this.handleEventLayoutChange}
                                            viewType={videoRecallViewType}
                                        />
                                    </div>
                                ),
                            },
                        }}
                        mobileProps={{
                            drawerIsOpen: this.state.showMobileFilterDrawer,
                            onCloseDrawer: this.toggleFilters,
                            drawerTitle: 'Filter Videos',
                        }}
                    />
                    <div className={styles.content}>
                        {this.state.requestVideoVisible &&
                            !this.props.isVideoRecallPreviewOn &&
                            (this.state.isMobileView ? (
                                <>
                                    <VideoRecallRequestForm toggleRequestVideo={this.toggleRequestVideo} isMobileView={true} />
                                </>
                            ) : (
                                <></>
                            ))}
                        {this.state.isMobileView && this.state.showMobileFilterDrawer && (
                            <Backdrop className={styles.filterBackdrop} open={this.state.showMobileFilterDrawer} />
                        )}
                        {!this.state.isMobileView &&
                            !this.props.isVideoRecallPreviewOn && ( // split OFF - desktop view always shows request video form
                                <VideoRecallRequestForm
                                    toggleRequestVideo={(): void => {
                                        /*Do Nothing*/
                                    }}
                                    isMobileView={false}
                                />
                            )}
                        {
                            <div hidden={data.length === 0 && !shouldLoadNextPage}>
                                {videoRecallViewType === 'tile' ? (
                                    <VideoEventCardList
                                        items={data}
                                        promoteVideo={this.promoteVideo}
                                        onPromotedVideo={this.handleOnPromotedVideo}
                                        onSelectVideoEvent={this.props.onSelectVideoEvent}
                                        loadNextPage={this.loadVideoEventsOnScroll}
                                        morePagesToLoad={hasMorePagesToLoad}
                                        deleteVideoRecall={this.deleteVideoRecall}
                                        onDeletedVideo={this.handleOnDeletedVideo}
                                        updateVideoEventWorkflowStatus={this.updateVideoEventWorkflowStatus}
                                        videoRecallPreviewEnabled={this.props.isVideoRecallPreviewOn}
                                        isVideoRecallRedesignV2SplitOn={this.props.isVideoRecallRedesignV2SplitOn}
                                    />
                                ) : (
                                    <VideoEventList
                                        items={data}
                                        onGridSort={this.gridSort}
                                        onSelectVideoEvent={this.props.onSelectVideoEvent}
                                        loadNextPage={this.loadVideoEventsOnScroll}
                                        promoteVideo={this.promoteVideo}
                                        promoteVideoRedesign={this.promoteVideoRedesign}
                                        deleteVideoRecall={this.deleteVideoRecall}
                                        morePagesToLoad={hasMorePagesToLoad}
                                        videoRecallPreviewEnabled={this.props.isVideoRecallPreviewOn}
                                        isVideoRecallRedesignV2SplitOn={this.props.isVideoRecallRedesignV2SplitOn}
                                    />
                                )}
                            </div>
                        }
                        {data.length === 0 && !shouldLoadNextPage && (
                            <div className={styles.noVideos}>
                                {this.props.videoRecall.getVideosLoaded() ? (
                                    <div>No Recalled Video for the criteria selected</div>
                                ) : (
                                    <CircularProgress></CircularProgress>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {this.props.isVideoRecallPreviewOn && (
                    <RequestVideoDrawer
                        open={this.state.requestVideoVisible}
                        onClose={this.toggleRequestVideo}
                        onRequestSubmitted={this.getVideoRecalls}
                        pairings={this.props.deviceAssociation.getState().deviceAssociationResults.data || []}
                        config={this.props.config}
                    />
                )}
            </div>
        );
    }
}

export const VideoRecallPage = VideoEventService.inject(
    VideoRecallFilterService.inject(
        IntegrationPartnerDataService.inject(
            DeviceAssociationService.inject(ConfigService.inject(VideoRecallService.inject(_VideoRecallPage))),
        ),
    ),
);
